<template>
    <div>
        <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="container-title container-headline container-headline--no-image">
                <h2>Mapping the Shadowy World of Spyware and Digital Forensics Sales</h2>
            </div>
        </div>
        <div class="section" id="spywareChart">
            <div class="foreground">
                <p>
                    The global spyware and digital forensics industry is equipping democratic and authoritarian
                    governments with advanced intrusive capabilities. Now, more information about this opaque industry
                    is coming to light following an array of surveillance scandals.
                </p>
                <p>
                    We’ve created an interactive graph that identifies the key vendors and clients in the spyware
                    industry, highlighting relationships between governments and suppliers of intrusion software.
                    Explore these relationships and how regimes are using intrusion technology by selecting a country or
                    vendor below.
                </p>
            </div>
        </div>
        <div class="section interactive">
            <div id="chartdiv"></div>
            <div class="legend">
                <div class="legend-value spyware">
                    <div class="color"></div>
                    <span>Spyware</span>
                </div>
                <div class="legend-value digital-forensic">
                    <div class="color"></div>
                    <span>Digital Forensics</span>
                </div>
                <div class="legend-value country">
                    <div class="color"></div>
                    <span>Country Deploying Surveillance Technology</span>
                </div>
            </div>
            <div id="info"></div>
        </div>
        <div class="section">
            <div class="foreground">
                <h4>Updated February 27, 2023</h4>
                <p><em>Steven Feldstein and Brian Kot</em></p>
                <p>
                    The dataset focuses on three overarching questions: Which governments show evidence of procuring and
                    using commercial spyware? Which private sector companies are involved and what are their countries
                    of origin? What activities have governments used the technology for?
                </p>
                <p>
                    Source material derives from the Citizen Lab, Freedom House, Privacy International, the Council on
                    Foreign Relations’ Cyber Operations Tracker, the Electronic Frontier Foundation, Article 19, Access
                    Now, and an assortment of related research organizations. The inventory also includes data from
                    major print and news media outlets (such as the <em>New York Times</em>, <em>Reuters</em>,
                    <em>Haaretz</em>, the <em>Financial Times</em>, the<em>Wall Street Journal</em>). The inventory
                    focuses on incidents occurring between 2011 and 2023.
                </p>
                <div class="section center">
                    <a
                        class="button button-big"
                        href="https://ceipfiles.s3.amazonaws.com/pdf/Global+inventory+spyware+and+digital+forensics+11Jan23.xlsx"
                        >Download the dataset
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
useHead({
    script: [
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/index.js' },
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/flow.js' },
        { innerHTML: '', src: 'https://cdn.amcharts.com/lib/5/themes/Animated.js' },
    ],
});
import { onMounted, ref } from 'vue';

onMounted(() => {
    callChart();
});

let spywareColor = '#27aae1';
let digitalForensicsColor = '#ef4136';
let countryColor = '#00a79d';

function callChart() {
    am5.ready(function () {
        // Create root element
        // https://www.amcharts.com/docs/v5/getting-started/#Root_element
        let root = am5.Root.new('chartdiv');

        // Set themes
        // https://www.amcharts.com/docs/v5/concepts/themes/
        root.setThemes([am5themes_Animated.new(root)]);

        // Create series
        // https://www.amcharts.com/docs/v5/charts/flow-charts/
        let series = root.container.children.push(
            am5flow.ChordNonRibbon.new(root, {
                sourceIdField: 'from',
                targetIdField: 'to',
                nameField: 'name',
                valueField: 'value',
                fillField: 'color',
                linkType: 'line',
                radius: am5.percent(70),
            }),
        );

        series.nodes.labels.template.setAll({
            fill: am5.color(0xffffff),
            textType: 'radial',
            centerX: 0,
            fontSize: 9,
        });

        // this adds an invisible background on the labels so they can be recognized as clickable targets
        series.nodes.labels.template.setup = function (target) {
            target.set(
                'background',
                am5.Rectangle.new(root, {
                    fill: am5.color(0x000000),
                    fillOpacity: 0,
                }),
            );
        };

        series.nodes.labels.template.adapters.add('fill', function (fill, target) {
            if (
                target.dataItem &&
                (target.dataItem.dataContext.id == 'Angola' ||
                    target.dataItem.dataContext.id == 'Argentina' ||
                    target.dataItem.dataContext.id == 'Armenia' ||
                    target.dataItem.dataContext.id == 'Azerbaijan' ||
                    target.dataItem.dataContext.id == 'Bahrain' ||
                    target.dataItem.dataContext.id == 'Bangladesh' ||
                    target.dataItem.dataContext.id == 'Belarus' ||
                    target.dataItem.dataContext.id == 'Belgium' ||
                    target.dataItem.dataContext.id == 'Botswana' ||
                    target.dataItem.dataContext.id == 'Brazil' ||
                    target.dataItem.dataContext.id == 'Bulgaria' ||
                    target.dataItem.dataContext.id == 'Canada' ||
                    target.dataItem.dataContext.id == 'Chile' ||
                    target.dataItem.dataContext.id == 'China' ||
                    target.dataItem.dataContext.id == 'Colombia' ||
                    target.dataItem.dataContext.id == "Côte d'Ivoire" ||
                    target.dataItem.dataContext.id == 'Cyprus' ||
                    target.dataItem.dataContext.id == 'Djibouti' ||
                    target.dataItem.dataContext.id == 'Ecuador' ||
                    target.dataItem.dataContext.id == 'Egypt' ||
                    target.dataItem.dataContext.id == 'El Salvador' ||
                    target.dataItem.dataContext.id == 'Estonia' ||
                    target.dataItem.dataContext.id == 'Ethiopia' ||
                    target.dataItem.dataContext.id == 'Gabon' ||
                    target.dataItem.dataContext.id == 'Germany' ||
                    target.dataItem.dataContext.id == 'Ghana' ||
                    target.dataItem.dataContext.id == 'Greece' ||
                    target.dataItem.dataContext.id == 'Honduras' ||
                    target.dataItem.dataContext.id == 'Hong Kong' ||
                    target.dataItem.dataContext.id == 'Hungary' ||
                    target.dataItem.dataContext.id == 'India' ||
                    target.dataItem.dataContext.id == 'Indonesia' ||
                    target.dataItem.dataContext.id == 'Israel' ||
                    target.dataItem.dataContext.id == 'Italy' ||
                    target.dataItem.dataContext.id == 'Jordan' ||
                    target.dataItem.dataContext.id == 'Kazakhstan' ||
                    target.dataItem.dataContext.id == 'Kenya' ||
                    target.dataItem.dataContext.id == 'Lebanon' ||
                    target.dataItem.dataContext.id == 'Madagascar' ||
                    target.dataItem.dataContext.id == 'Malaysia' ||
                    target.dataItem.dataContext.id == 'Mexico' ||
                    target.dataItem.dataContext.id == 'Mongolia' ||
                    target.dataItem.dataContext.id == 'Morocco' ||
                    target.dataItem.dataContext.id == 'Myanmar' ||
                    target.dataItem.dataContext.id == 'Netherlands' ||
                    target.dataItem.dataContext.id == 'Nigeria' ||
                    target.dataItem.dataContext.id == 'Oman' ||
                    target.dataItem.dataContext.id == 'Pakistan' ||
                    target.dataItem.dataContext.id == 'Panama' ||
                    target.dataItem.dataContext.id == 'Paraguay' ||
                    target.dataItem.dataContext.id == 'Philippines' ||
                    target.dataItem.dataContext.id == 'Poland' ||
                    target.dataItem.dataContext.id == 'Romania' ||
                    target.dataItem.dataContext.id == 'Russia' ||
                    target.dataItem.dataContext.id == 'Rwanda' ||
                    target.dataItem.dataContext.id == 'Saudi Arabia' ||
                    target.dataItem.dataContext.id == 'Serbia' ||
                    target.dataItem.dataContext.id == 'Singapore' ||
                    target.dataItem.dataContext.id == 'Slovenia' ||
                    target.dataItem.dataContext.id == 'South Africa' ||
                    target.dataItem.dataContext.id == 'Spain' ||
                    target.dataItem.dataContext.id == 'Sudan' ||
                    target.dataItem.dataContext.id == 'Switzerland' ||
                    target.dataItem.dataContext.id == 'Thailand' ||
                    target.dataItem.dataContext.id == 'Togo' ||
                    target.dataItem.dataContext.id == 'Turkey' ||
                    target.dataItem.dataContext.id == 'Turkmenistan' ||
                    target.dataItem.dataContext.id == 'Uganda' ||
                    target.dataItem.dataContext.id == 'United Arab Emirates' ||
                    target.dataItem.dataContext.id == 'United States' ||
                    target.dataItem.dataContext.id == 'Uzbekistan' ||
                    target.dataItem.dataContext.id == 'Venezuela' ||
                    target.dataItem.dataContext.id == 'Vietnam' ||
                    target.dataItem.dataContext.id == 'Zambia')
            ) {
                return am5.color(0x929497);
            } else {
                return fill;
            }
        });

        series.nodes.get('colors').set('colors', [am5.color(0x27aae1), am5.color(0x812160), am5.color(0x00a79d)]);

        series.links.template.setAll({
            fillStyle: 'source',
            tooltipText: 'From: [bold]{from}[/]\nTo: [bold]{to}',
        });

        function closeButton() {
            // close button in info
            document.querySelector('#closeInfo').onclick = function (e) {
                document.getElementById('info').classList.remove('active');
            };
        }

        function showOverlay(id) {
            // making info box visible
            document.querySelector('#info').classList.add('active');
            let html = '';

            //gathering data
            if (id == 'AccessData') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Botswana, Colombia, Nigeria, United States';
            } else if (id == 'Black Cube') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Hungary';
            } else if (id == 'BlackBag') {
                html +=
                    '<strong>Country of Origin:</strong> United States, Israel<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> United States';
            } else if (id == 'Candiru') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Hungary, Indonesia, Saudi Arabia, Spain, United Arab Emirates, Uzbekistan';
            } else if (id == 'Cellebrite') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Argentina, Bahrain, Bangladesh, Belarus, Botswana, Brazil, China, Germany, Ghana, Honduras, Hong Kong, India, Indonesia, Morocco, Myanmar, Nigeria, Russia, Saudi Arabia, Turkey, Uganda, United Arab Emirates, United States, Venezuela';
            } else if (id == 'Cyberbit') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Ethiopia';
            } else if (id == 'Cytrox') {
                html +=
                    "<strong>Country of Origin:</strong> Israel, Hungary<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Armenia, Colombia, Côte d'Ivoire, Egypt, Greece, Indonesia, Madagascar, Oman, Philippines, Saudi Arabia, Serbia, Spain, Sudan, Vietnam";
            } else if (id == 'DarkMatter') {
                html +=
                    '<strong>Country of Origin:</strong> United Arab Emirates<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> United Arab Emirates';
            } else if (id == 'Dark Caracal') {
                html +=
                    '<strong>Country of Origin:</strong> Lebanon<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Lebanon';
            } else if (id == 'DigiTask') {
                html +=
                    '<strong>Country of Origin:</strong> Germany<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Germany, Netherlands, Switzerland';
            } else if (id == 'EaseUS') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Elcomsoft') {
                html +=
                    '<strong>Country of Origin:</strong> Russia<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar, United States';
            } else if (id == 'eSurv') {
                html +=
                    '<strong>Country of Origin:</strong> Italy<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Italy';
            } else if (id == 'Fiberhome') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> China';
            } else if (id == 'FinFisher') {
                html +=
                    '<strong>Country of Origin:</strong> Germany<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Angola, Bahrain, Bangladesh, Belgium, Bulgaria, Egypt, Ethiopia, Gabon, Indonesia, Italy, Jordan, Kazakhstan, Kenya, Lebanon, Malaysia, Mexico, Mongolia, Morocco, Nigeria, Oman, Pakistan, Paraguay, Romania, Saudi Arabia, Serbia, Singapore, Slovenia, South Africa, Turkey, Turkmenistan, Uganda, United Arab Emirates, Venezuela, Vietnam';
            } else if (id == 'Grayshift') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Belarus, United States';
            } else if (id == 'Hacking Team') {
                html +=
                    '<strong>Country of Origin:</strong> Italy<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Azerbaijan, Brazil, Chile, Colombia, Cyprus, Ecuador, Egypt, Ethiopia, Honduras, Hungary, Italy, Kazakhstan, Lebanon, Malaysia, Mexico, Morocco, Nigeria, Oman, Panama, Poland, Russia, Saudi Arabia, Singapore, Spain, Sudan, Thailand, United Arab Emirates, United States, Uzbekistan';
            } else if (id == 'iMyFone#') {
                html +=
                    '<strong>Country of Origin:</strong> Hong Kong<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Magnet Forensics') {
                html +=
                    '<strong>Country of Origin:</strong> Canada<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar, United States';
            } else if (id == 'Meiya Pico') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> China, Egypt, Philippines, Russia';
            } else if (id == 'Mollitiam') {
                html +=
                    '<strong>Country of Origin:</strong> Spain<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Colombia';
            } else if (id == 'MSAB') {
                html +=
                    '<strong>Country of Origin:</strong> Sweden<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Hong Kong, Morocco, Myanmar, United States';
            } else if (id == 'NSO Group') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Azerbaijan, Bahrain, Bangladesh, Belgium, Djibouti, El Salvador, Estonia, Germany, Ghana, Hungary, India, Indonesia, Israel, Jordan, Kazakhstan, Kenya, Mexico, Morocco, Netherlands, Panama, Poland, Rwanda, Saudi Arabia, Spain, Thailand, Togo, Uganda, United Arab Emirates, Uzbekistan, Zambia';
            } else if (id == 'OpenText') {
                html +=
                    '<strong>Country of Origin:</strong> Canada<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Oxygen Software') {
                html +=
                    '<strong>Country of Origin:</strong> Russia<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Belarus, Kazakhstan, Myanmar, United States, Uzbekistan';
            } else if (id == 'Paraben') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> United States';
            } else if (id == 'Paragon') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> United States';
            } else if (id == 'Passware') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar, United States';
            } else if (id == 'Quadream') {
                html +=
                    '<strong>Country of Origin:</strong> Israel<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Mexico, Saudi Arabia, Singapore';
            } else if (id == 'RCS Labs') {
                html +=
                    '<strong>Country of Origin:</strong> Italy<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Italy, Kazakhstan';
            } else if (id == 'Resonant') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> China';
            } else if (id == 'SalvationData') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'SecurCube') {
                html +=
                    '<strong>Country of Origin:</strong> Italy<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Silicon Forensics') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Sirchie') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Susteen') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> United States';
            } else if (id == 'SysTools') {
                html +=
                    '<strong>Country of Origin:</strong> United States<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> Myanmar';
            } else if (id == 'Undisclosed') {
                html +=
                    '<strong>Country of Origin:</strong> Undisclosed<br /><br /><strong>Category of Technology:</strong> Spyware<br /><br /><strong>Country Where Deployed:</strong> Canada';
            } else if (id == 'Zhongke Ronghui Security Technology') {
                html +=
                    '<strong>Country of Origin:</strong> China<br /><br /><strong>Category of Technology:</strong> Digital forensics<br /><br /><strong>Country Where Deployed:</strong> China';
            } else if (id == 'Angola') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Implicated in targeting of activists';
            } else if (id == 'Argentina') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Cellebrite<br /><br /><strong>Description:</strong> Supplied federal security forces with tools for hacking into locked mobile devices since the early 2010s';
            } else if (id == 'Armenia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Cytrox<br /><br /><strong>Description:</strong> Purchased by government-backed actors';
            } else if (id == 'Azerbaijan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, NSO Group<br /><br /><strong>Description:</strong> Evidence indicates that government operators has used surveillance technology to spy on civil society since 2009';
            } else if (id == 'Bahrain') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, FinFisher, NSO Group<br /><br /><strong>Description:</strong> Cellebrite software used to prosecute and torture dissidents; Arab Spring activists hacked';
            } else if (id == 'Bangladesh') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, FinFisher, NSO Group<br /><br /><strong>Description:</strong> Intrusion technology purchased by Bangladesh’s Rapid Action Battalion, which has a record of abductions, torture, and disappearances';
            } else if (id == 'Belarus') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, Grayshift, Oxygen Software<br /><br /><strong>Description:</strong> Used intrusion software to hack activists and journalists';
            } else if (id == 'Belgium') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> FinFisher, NSO Group<br /><br /><strong>Description:</strong> Belgian Federal Police linked to acquisition of FinFisher and Pegasus spyware';
            } else if (id == 'Botswana') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> AccessData, Cellebrite<br /><br /><strong>Description:</strong> Police used software extraction to investigate journalist sources';
            } else if (id == 'Brazil') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Cellebrite, Hacking Team<br /><br /><strong>Description:</strong> History of surveillance abuses; infiltration of online platforms and political monitoring are common';
            } else if (id == 'Bulgaria') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Identified intrusion server registered to the Bulgarian Ministry of State Administration and Administrative Reform';
            } else if (id == 'Canada') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Undisclosed<br /><br /><strong>Description:</strong> Canadian national police used spyware in ten investigations between 2018 and 2020';
            } else if (id == 'Chile') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Hacking Team<br /><br /><strong>Description:</strong> Investigations Police paid 2.2 million euros to buy RCS Lab spyware';
            } else if (id == 'China') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, Fiberhome, Meiya Pico, Resonant, Zhongke Ronghui<br /><br /><strong>Description:</strong> Xinjiang visitors forced to download Fengcai spyware app; widespread digital forensics use by police';
            } else if (id == 'Colombia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> AccessData, Cytrox, Hacking Team, Mollitiam<br /><br /><strong>Description:</strong> Administrative Department of Security agents used mobile forensic units to obtain private data from devices; conducted surveillance of regime opponents';
            } else if (id == "Côte d'Ivoire") {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cytrox<br /><br /><strong>Description:</strong> Likely government actor purchaser of Cytrox exploits';
            } else if (id == 'Cyprus') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Hacking Team<br /><br /><strong>Description:</strong> Head of intelligence service stepped down due to Hacking Team breach';
            } else if (id == 'Djibouti') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> During the Trump administration, the U.S. Central Intelligence Agency purchased Pegasus for Djibouti, which used the tool for at least a year';
            } else if (id == 'Ecuador') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Hacking Team<br /><br /><strong>Description:</strong> Leaked documents exposed illegal spying on politicians, journalists, and activists';
            } else if (id == 'Egypt') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cytrox, FinFisher, Hacking Team, Meiya Pico, NSO Group<br /><br /><strong>Description:</strong> Malware campaigns against civil society';
            } else if (id == 'El Salvador') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Pegasus spyware used against journalists and activists';
            } else if (id == 'Estonia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Israel authorized Estonia to acquire Pegasus in 2018; Estonia made a down payment of $30 million for the system';
            } else if (id == 'Ethiopia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cyberbit, FinFisher, Hacking Team<br /><br /><strong>Description:</strong> Targeted Ethiopian dissidents residing in the United States, United Kingdom, and other countries; targeted opposition and civil society in  Ethiopia';
            } else if (id == 'Gabon') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Targeted opposition members and civil society';
            } else if (id == 'Germany') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Cellebrite, DigiTask, NSO Group<br /><br /><strong>Description:</strong> Police unit prosecuting online hate speech can access Cellebrite to break into phones; federal criminal police purchased Pegasus for select antiterrorist and anti–organized crime operations';
            } else if (id == 'Ghana') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Cellebrite, NSO Group<br /><br /><strong>Description:</strong> Possible use against journalists; allegedly planned to use Pegasus to snoop on the opposition ahead of the 2017 election';
            } else if (id == 'Greece') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Cytrox<br /><br /><strong>Description:</strong> Intelligence services used Predator spyware to spy on MEP Androulakis and investigative journalists';
            } else if (id == 'Honduras') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, Hacking Team<br /><br /><strong>Description:</strong> Intrusion technologies acquired by Honduras’s police';
            } else if (id == 'Hong Kong') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, MSAB<br /><br /><strong>Description:</strong> Law enforcement in China and Hong Kong continue to acquire Cellebrite’s UFED product, allowing officers to break into phones and siphon data';
            } else if (id == 'Hungary') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Black Cube, Candiru, Hacking Team, NSO Group<br /><br /><strong>Description:</strong> Black Cube involved in campaign to discredit nongovernmental organizations ahead of elections; Pegasus used to monitor journalists, media company owners, lawyers, opposition, and government officials';
            } else if (id == 'India') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, NSO Group<br /><br /><strong>Description:</strong> Used spyware to target hundreds of journalists, activists, opposition politicians, government officials, and business executives';
            } else if (id == 'Indonesia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Candiru, Cellebrite, Cytrox, FinFisher, NSO Group<br /><br /><strong>Description:</strong> Persecuted LGBT population, religious minorities';
            } else if (id == 'Israel') {
                html +=
                    "<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Police reportedly used Pegasus against antigovernment protests, senior politicians, mayors, and employees of government-owned companies; Pegasus also found on Palestinian activists' cellphones";
            } else if (id == 'Italy') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> eSurv, FinFisher, Hacking Team, RCS Labs<br /><br /><strong>Description:</strong> State police used spyware; concerns that intel agencies are intercepting personal communications employing hacking without statutory authorization or safeguards';
            } else if (id == 'Jordan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> FinFisher, NSO Group<br /><br /><strong>Description:</strong> Used malware to spy on journalists, human rights defenders, and opposition';
            } else if (id == 'Kazakhstan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Hacking Team, NSO Group, Oxygen Software, RCS Labs<br /><br /><strong>Description:</strong> Obtained software to monitor and interfere with online traffic and perform targeted cyber attacks against users and devices; dozens of government and business persons surveilled';
            } else if (id == 'Kenya') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, NSO Group<br /><br /><strong>Description:</strong> Spyware reportedly used to repress civil society organizations and human rights defenders; linked to National Security Intelligence';
            } else if (id == 'Lebanon') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Dark Caracal, FinFisher, Hacking Team<br /><br /><strong>Description:</strong> Developed unique mobile surveillance tool, Dark Caracal/Pallas, to extract data from Android devices';
            } else if (id == 'Madagascar') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cytrox<br /><br /><strong>Description:</strong> Likely government-backed actors purchased Cytrox exploits';
            } else if (id == 'Malaysia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Hacking Team<br /><br /><strong>Description:</strong> Citizen Lab discovered a booby-trapped document that contained a candidate list for 2013 Malaysian general elections';
            } else if (id == 'Mexico') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher, Hacking Team, NSO Group, Quadream<br /><br /><strong>Description:</strong> Reporters and activists hacked with NSO Group spyware';
            } else if (id == 'Mongolia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Linkages between FinFisher malware and State Special Security Department';
            } else if (id == 'Morocco') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Hacking Team, NSO Group, Cellebrite, MSAB<br /><br /><strong>Description:</strong> Supreme Council of National Defense allegedly used spyware; RCS Lab spyware used against Moroccan media outlet Mamfakinch';
            } else if (id == 'Myanmar') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, MSAB, OpenText, Magnet Forensics, SecurCube, SalvationData, EaseUS, iMyFone, Elcomsoft, Silicon Forensics, Sirchie, Passware, Oxygen Software, SysTools<br /><br /><strong>Description:</strong> Used Cellebrite to collect data from journalists’ smartphones; telecommunications companies ordered to install intercept spyware';
            } else if (id == 'Netherlands') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> DigiTask, NSO Group<br /><br /><strong>Description:</strong> DigiTask sold spyware to public authorities; police and security service used Pegasus to track down a crime suspect';
            } else if (id == 'Nigeria') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, AccessData, Hacking Team, FinFisher<br /><br /><strong>Description:</strong> Intrusion software used to spy on politicians and regime opponents; Hacking Team worked with the governor of the state of Bayelsa; Nigerian security forces also purchased spyware';
            } else if (id == 'Oman') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, FinFisher, Cytrox<br /><br /><strong>Description:</strong> Ministry of Interior linked to targeted surveillance';
            } else if (id == 'Pakistan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Used malware to infect PowerPoint documents and steal files from targeted computers';
            } else if (id == 'Panama') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Hacking Team, NSO Group<br /><br /><strong>Description:</strong> Ex-president accused of using Pegasus to spy on political enemies, business rivals, and even a mistress; used software to track 150 people illegally';
            } else if (id == 'Paraguay') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Used spyware on journalists';
            } else if (id == 'Philippines') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cytrox, Meiya Pico<br /><br /><strong>Description:</strong> Identified as a Cytrox customer';
            } else if (id == 'Poland') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> Hacking Team, NSO Group<br /><br /><strong>Description:</strong> Law and Justice party’s leader admitted purchasing Pegasus, which was used against various opposition leaders';
            } else if (id == 'Romania') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Purchase linked to government actors';
            } else if (id == 'Russia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cellebrite, Hacking Team, Meiya Pico<br /><br /><strong>Description:</strong> Online accounts of journalists and civil society activists often compromised, indicating a coordinated campaign to access their data';
            } else if (id == 'Rwanda') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Security officials authorized to tap online communications; Pegasus targeted Rwandan dissidents and Belgian journalists';
            } else if (id == 'Saudi Arabia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, NSO Group, FinFisher, Candiru, Cytrox, Quadream, Cellebrite<br /><br /><strong>Description:</strong> Extensive documented abuses of spyware to target political opponents and civil society';
            } else if (id == 'Serbia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Cytrox, FinFisher<br /><br /><strong>Description:</strong> FinFisher use linked to Security Information Agency';
            } else if (id == 'Singapore') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Quadream, Hacking Team<br /><br /><strong>Description:</strong> Legal framework regulating communications interception falls short of international human rights standards; oversight is nonexistent';
            } else if (id == 'Slovenia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Identified as a FinFisher client';
            } else if (id == 'South Africa') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> ED<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Reportedly conducted surveillance of activists, journalists, and political opponents';
            } else if (id == 'Spain') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> NSO Group, Candiru, Cytrox, Hacking Team<br /><br /><strong>Description:</strong> Government targeted Catalan politicians';
            } else if (id == 'Sudan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Cytrox, Hacking Team<br /><br /><strong>Description:</strong> Allegedly equipped the Rapid Support Forces with sophisticated surveillance technology';
            } else if (id == 'Switzerland') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> DigiTask<br /><br /><strong>Description:</strong> DigiTask sold spyware to public authorities in Switzerland';
            } else if (id == 'Thailand') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, NSO Group<br /><br /><strong>Description:</strong> Engaged in targeted surveillance against civil society, regime opponents';
            } else if (id == 'Togo') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Targeted Togo civil society during nationwide pro-reform protests, which the state forcibly dispersed';
            } else if (id == 'Turkey') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, FinFisher, Cellebrite<br /><br /><strong>Description:</strong> Taps and intercepts most forms of telecommunication';
            } else if (id == 'Turkmenistan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher<br /><br /><strong>Description:</strong> Conducted targeted surveillance against its citizens';
            } else if (id == 'Uganda') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, NSO Group, Cellebrite<br /><br /><strong>Description:</strong> Used spyware against opposition leaders, media, and establishment insiders';
            } else if (id == 'United Arab Emirates') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, Candiru, DarkMatter, FinFisher, NSO Group, Cellebrite<br /><br /><strong>Description:</strong> Surveilled newspaper editor; tracked regime opponents';
            } else if (id == 'United States') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> LD<br /><br /><strong>Commercial Entity:</strong> Cellebrite, AccessData, BlackBag, Grayshift, Hacking Team, Magnet Forensics, MSAB, Oxygen Software, Paraben, Paragon, Passware, Elcomsoft, Susteen<br /><br /><strong>Description:</strong> Federal Bureau of Investigation (FBI) has purchased at least $2 million worth of Cellebrite products since 2012; FBI, Drug Enforcement Administration, and the U.S. Army implicated in dealings with Hacking Team';
            } else if (id == 'Uzbekistan') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> Hacking Team, Candiru, Oxygen Software, NSO Group<br /><br /><strong>Description:</strong> Deployed invasive software to hijack devices';
            } else if (id == 'Venezuela') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Cellebrite<br /><br /><strong>Description:</strong> Conducted widespread targeting of journalists, opposition';
            } else if (id == 'Vietnam') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> CA<br /><br /><strong>Commercial Entity:</strong> FinFisher, Cytrox<br /><br /><strong>Description:</strong> Likely government-backed actors purchased exploits';
            } else if (id == 'Zambia') {
                html +=
                    '<strong>V-Dem Regime Type 2021:</strong> EA<br /><br /><strong>Commercial Entity:</strong> NSO Group<br /><br /><strong>Description:</strong> Government linked to Pegasus purchase';
            }

            //printing data
            info.innerHTML =
                "<div class='close-panel' id='closeInfo'><span class='ceip-icon icon-cancel-circle'>X</span></div>";
            if (html) {
                info.innerHTML +=
                    '<div class="description"><div class="title">' +
                    id +
                    '</div><div class="info-text">' +
                    html +
                    '</div></div>';
            } else {
                info.innerHTML +=
                    '<div class="description"><div class="title">' +
                    id +
                    '</div><div class="info-text"><i>No description provided.</i></div></div>';
            }

            // close button in info
            closeButton();
        }

        // series.links.template.states.create("highlight", {
        // 	strokeWidth: 2
        //   });

        series.set({
            interactive: true,
            interactiveChildren: true,
        });

        series.nodes.rectangles.template.events.on('click', function (ev) {
            showOverlay(ev.target._dataItem._settings.id);
        });

        series.nodes.labels.template.events.on('click', function (ev) {
            showOverlay(ev.target._dataItem._settings.id);
        });

        series.nodes.data.setAll([
            { id: 'AccessData', fill: am5.color(digitalForensicsColor) },
            { id: 'BlackBag', fill: am5.color(digitalForensicsColor) },
            { id: 'Cellebrite', fill: am5.color(digitalForensicsColor) },
            { id: 'EaseUS', fill: am5.color(digitalForensicsColor) },
            { id: 'Elcomsoft', fill: am5.color(digitalForensicsColor) },
            { id: 'Fiberhome', fill: am5.color(digitalForensicsColor) },
            { id: 'Grayshift', fill: am5.color(digitalForensicsColor) },
            { id: 'iMyFone', fill: am5.color(digitalForensicsColor) },
            { id: 'Magnet Forensics', fill: am5.color(digitalForensicsColor) },
            { id: 'Meiya Pico', fill: am5.color(digitalForensicsColor) },
            { id: 'MSAB', fill: am5.color(digitalForensicsColor) },
            { id: 'OpenText', fill: am5.color(digitalForensicsColor) },
            { id: 'Oxygen Software', fill: am5.color(digitalForensicsColor) },
            { id: 'Paraben', fill: am5.color(digitalForensicsColor) },
            { id: 'Passware', fill: am5.color(digitalForensicsColor) },
            { id: 'Resonant', fill: am5.color(digitalForensicsColor) },
            { id: 'SalvationData', fill: am5.color(digitalForensicsColor) },
            { id: 'SecurCube', fill: am5.color(digitalForensicsColor) },
            { id: 'Sillicon Forensics', fill: am5.color(digitalForensicsColor) },
            { id: 'Sirchie', fill: am5.color(digitalForensicsColor) },
            { id: 'Susteen', fill: am5.color(digitalForensicsColor) },
            { id: 'SysTools', fill: am5.color(digitalForensicsColor) },
            { id: 'Zhongke Ronghui Security Technology', fill: am5.color(digitalForensicsColor) },
            { id: 'Black Cube', fill: am5.color(spywareColor) },
            { id: 'Candiru', fill: am5.color(spywareColor) },
            { id: 'Cyberbit', fill: am5.color(spywareColor) },
            { id: 'Cytrox', fill: am5.color(spywareColor) },
            { id: 'Dark Caracal', fill: am5.color(spywareColor) },
            { id: 'DarkMatter', fill: am5.color(spywareColor) },
            { id: 'DigiTask', fill: am5.color(spywareColor) },
            { id: 'eSurv', fill: am5.color(spywareColor) },
            { id: 'FinFisher', fill: am5.color(spywareColor) },
            { id: 'Hacking Team', fill: am5.color(spywareColor) },
            { id: 'Mollitiam', fill: am5.color(spywareColor) },
            { id: 'NSO Group', fill: am5.color(spywareColor) },
            { id: 'Paragon', fill: am5.color(spywareColor) },
            { id: 'Quadream', fill: am5.color(spywareColor) },
            { id: 'RCS Labs', fill: am5.color(spywareColor) },
            { id: 'Undisclosed', fill: am5.color(spywareColor) },
            { id: 'Angola', fill: am5.color(countryColor) },
            { id: 'Argentina', fill: am5.color(countryColor) },
            { id: 'Armenia', fill: am5.color(countryColor) },
            { id: 'Azerbaijan', fill: am5.color(countryColor) },
            { id: 'Bahrain', fill: am5.color(countryColor) },
            { id: 'Bangladesh', fill: am5.color(countryColor) },
            { id: 'Belarus', fill: am5.color(countryColor) },
            { id: 'Belgium', fill: am5.color(countryColor) },
            { id: 'Botswana', fill: am5.color(countryColor) },
            { id: 'Brazil', fill: am5.color(countryColor) },
            { id: 'Bulgaria', fill: am5.color(countryColor) },
            { id: 'Canada', fill: am5.color(countryColor) },
            { id: 'Chile', fill: am5.color(countryColor) },
            { id: 'China', fill: am5.color(countryColor) },
            { id: 'Colombia', fill: am5.color(countryColor) },
            { id: "Côte d'Ivoire", fill: am5.color(countryColor) },
            { id: 'Cyprus', fill: am5.color(countryColor) },
            { id: 'Djibouti', fill: am5.color(countryColor) },
            { id: 'Ecuador', fill: am5.color(countryColor) },
            { id: 'Egypt', fill: am5.color(countryColor) },
            { id: 'El Salvador', fill: am5.color(countryColor) },
            { id: 'Estonia', fill: am5.color(countryColor) },
            { id: 'Ethiopia', fill: am5.color(countryColor) },
            { id: 'Gabon', fill: am5.color(countryColor) },
            { id: 'Germany', fill: am5.color(countryColor) },
            { id: 'Ghana', fill: am5.color(countryColor) },
            { id: 'Greece', fill: am5.color(countryColor) },
            { id: 'Honduras', fill: am5.color(countryColor) },
            { id: 'Hong Kong', fill: am5.color(countryColor) },
            { id: 'Hungary', fill: am5.color(countryColor) },
            { id: 'India', fill: am5.color(countryColor) },
            { id: 'Indonesia', fill: am5.color(countryColor) },
            { id: 'Israel', fill: am5.color(countryColor) },
            { id: 'Italy', fill: am5.color(countryColor) },
            { id: 'Jordan', fill: am5.color(countryColor) },
            { id: 'Kazakhstan', fill: am5.color(countryColor) },
            { id: 'Kenya', fill: am5.color(countryColor) },
            { id: 'Lebanon', fill: am5.color(countryColor) },
            { id: 'Madagascar', fill: am5.color(countryColor) },
            { id: 'Malaysia', fill: am5.color(countryColor) },
            { id: 'Mexico', fill: am5.color(countryColor) },
            { id: 'Mongolia', fill: am5.color(countryColor) },
            { id: 'Morocco', fill: am5.color(countryColor) },
            { id: 'Myanmar', fill: am5.color(countryColor) },
            { id: 'Netherlands', fill: am5.color(countryColor) },
            { id: 'Nigeria', fill: am5.color(countryColor) },
            { id: 'Oman', fill: am5.color(countryColor) },
            { id: 'Pakistan', fill: am5.color(countryColor) },
            { id: 'Panama', fill: am5.color(countryColor) },
            { id: 'Paraguay', fill: am5.color(countryColor) },
            { id: 'Philippines', fill: am5.color(countryColor) },
            { id: 'Poland', fill: am5.color(countryColor) },
            { id: 'Romania', fill: am5.color(countryColor) },
            { id: 'Russia', fill: am5.color(countryColor) },
            { id: 'Rwanda', fill: am5.color(countryColor) },
            { id: 'Saudi Arabia', fill: am5.color(countryColor) },
            { id: 'Serbia', fill: am5.color(countryColor) },
            { id: 'Singapore', fill: am5.color(countryColor) },
            { id: 'Slovenia', fill: am5.color(countryColor) },
            { id: 'South Africa', fill: am5.color(countryColor) },
            { id: 'Spain', fill: am5.color(countryColor) },
            { id: 'Sudan', fill: am5.color(countryColor) },
            { id: 'Switzerland', fill: am5.color(countryColor) },
            { id: 'Thailand', fill: am5.color(countryColor) },
            { id: 'Togo', fill: am5.color(countryColor) },
            { id: 'Turkey', fill: am5.color(countryColor) },
            { id: 'Turkmenistan', fill: am5.color(countryColor) },
            { id: 'Uganda', fill: am5.color(countryColor) },
            { id: 'United Arab Emirates', fill: am5.color(countryColor) },
            { id: 'United States', fill: am5.color(countryColor) },
            { id: 'Uzbekistan', fill: am5.color(countryColor) },
            { id: 'Venezuela', fill: am5.color(countryColor) },
            { id: 'Vietnam', fill: am5.color(countryColor) },
            { id: 'Zambia', fill: am5.color(countryColor) },
        ]);

        // Set data
        // https://www.amcharts.com/docs/v5/charts/flow-charts/#Setting_data
        series.data.setAll([
            { from: 'AccessData', to: 'Botswana', value: 1 },
            { from: 'AccessData', to: 'Colombia', value: 1 },
            { from: 'AccessData', to: 'Nigeria', value: 1 },
            { from: 'AccessData', to: 'United States', value: 1 },
            { from: 'Black Cube', to: 'Hungary', value: 1 },
            { from: 'BlackBag', to: 'United States', value: 1 },
            { from: 'Candiru', to: 'Hungary', value: 1 },
            { from: 'Candiru', to: 'Indonesia', value: 1 },
            { from: 'Candiru', to: 'Saudi Arabia', value: 1 },
            { from: 'Candiru', to: 'Spain', value: 1 },
            { from: 'Candiru', to: 'United Arab Emirates', value: 1 },
            { from: 'Candiru', to: 'Uzbekistan', value: 1 },
            { from: 'Cellebrite', to: 'Argentina', value: 1 },
            { from: 'Cellebrite', to: 'Bahrain', value: 1 },
            { from: 'Cellebrite', to: 'Bangladesh', value: 1 },
            { from: 'Cellebrite', to: 'Belarus', value: 1 },
            { from: 'Cellebrite', to: 'Botswana', value: 1 },
            { from: 'Cellebrite', to: 'Brazil', value: 1 },
            { from: 'Cellebrite', to: 'China', value: 1 },
            { from: 'Cellebrite', to: 'Germany', value: 1 },
            { from: 'Cellebrite', to: 'Ghana', value: 1 },
            { from: 'Cellebrite', to: 'Honduras', value: 1 },
            { from: 'Cellebrite', to: 'Hong Kong', value: 1 },
            { from: 'Cellebrite', to: 'India', value: 1 },
            { from: 'Cellebrite', to: 'Indonesia', value: 1 },
            { from: 'Cellebrite', to: 'Morocco', value: 1 },
            { from: 'Cellebrite', to: 'Myanmar', value: 1 },
            { from: 'Cellebrite', to: 'Nigeria', value: 1 },
            { from: 'Cellebrite', to: 'Russia', value: 1 },
            { from: 'Cellebrite', to: 'Saudi Arabia', value: 1 },
            { from: 'Cellebrite', to: 'Turkey', value: 1 },
            { from: 'Cellebrite', to: 'Uganda', value: 1 },
            { from: 'Cellebrite', to: 'United Arab Emirates', value: 1 },
            { from: 'Cellebrite', to: 'United States', value: 1 },
            { from: 'Cellebrite', to: 'Venezuela', value: 1 },
            { from: 'Cyberbit', to: 'Ethiopia', value: 1 },
            { from: 'Cytrox', to: 'Armenia', value: 1 },
            { from: 'Cytrox', to: 'Colombia', value: 1 },
            { from: 'Cytrox', to: "Côte d'Ivoire", value: 1 },
            { from: 'Cytrox', to: 'Egypt', value: 1 },
            { from: 'Cytrox', to: 'Greece', value: 1 },
            { from: 'Cytrox', to: 'Indonesia', value: 1 },
            { from: 'Cytrox', to: 'Madagascar', value: 1 },
            { from: 'Cytrox', to: 'Oman', value: 1 },
            { from: 'Cytrox', to: 'Philippines', value: 1 },
            { from: 'Cytrox', to: 'Saudi Arabia', value: 1 },
            { from: 'Cytrox', to: 'Serbia', value: 1 },
            { from: 'Cytrox', to: 'Spain', value: 1 },
            { from: 'Cytrox', to: 'Sudan', value: 1 },
            { from: 'Cytrox', to: 'Vietnam', value: 1 },
            { from: 'DarkMatter', to: 'United Arab Emirates', value: 1 },
            { from: 'Dark Caracal', to: 'Lebanon', value: 1 },
            { from: 'DigiTask', to: 'Germany', value: 1 },
            { from: 'DigiTask', to: 'Netherlands', value: 1 },
            { from: 'DigiTask', to: 'Switzerland', value: 1 },
            { from: 'EaseUS', to: 'Myanmar', value: 1 },
            { from: 'Elcomsoft', to: 'Myanmar', value: 1 },
            { from: 'Elcomsoft', to: 'United States', value: 1 },
            { from: 'eSurv', to: 'Italy', value: 1 },
            { from: 'Fiberhome', to: 'China', value: 1 },
            { from: 'FinFisher', to: 'Angola', value: 1 },
            { from: 'FinFisher', to: 'Bahrain', value: 1 },
            { from: 'FinFisher', to: 'Bangladesh', value: 1 },
            { from: 'FinFisher', to: 'Belgium', value: 1 },
            { from: 'FinFisher', to: 'Bulgaria', value: 1 },
            { from: 'FinFisher', to: 'Egypt', value: 1 },
            { from: 'FinFisher', to: 'Ethiopia', value: 1 },
            { from: 'FinFisher', to: 'Gabon', value: 1 },
            { from: 'FinFisher', to: 'Indonesia', value: 1 },
            { from: 'FinFisher', to: 'Italy', value: 1 },
            { from: 'FinFisher', to: 'Jordan', value: 1 },
            { from: 'FinFisher', to: 'Kazakhstan', value: 1 },
            { from: 'FinFisher', to: 'Kenya', value: 1 },
            { from: 'FinFisher', to: 'Lebanon', value: 1 },
            { from: 'FinFisher', to: 'Malaysia', value: 1 },
            { from: 'FinFisher', to: 'Mexico', value: 1 },
            { from: 'FinFisher', to: 'Mongolia', value: 1 },
            { from: 'FinFisher', to: 'Morocco', value: 1 },
            { from: 'FinFisher', to: 'Nigeria', value: 1 },
            { from: 'FinFisher', to: 'Oman', value: 1 },
            { from: 'FinFisher', to: 'Pakistan', value: 1 },
            { from: 'FinFisher', to: 'Paraguay', value: 1 },
            { from: 'FinFisher', to: 'Romania', value: 1 },
            { from: 'FinFisher', to: 'Saudi Arabia', value: 1 },
            { from: 'FinFisher', to: 'Serbia', value: 1 },
            { from: 'FinFisher', to: 'Singapore', value: 1 },
            { from: 'FinFisher', to: 'Slovenia', value: 1 },
            { from: 'FinFisher', to: 'South Africa', value: 1 },
            { from: 'FinFisher', to: 'Turkey', value: 1 },
            { from: 'FinFisher', to: 'Turkmenistan', value: 1 },
            { from: 'FinFisher', to: 'Uganda', value: 1 },
            { from: 'FinFisher', to: 'United Arab Emirates', value: 1 },
            { from: 'FinFisher', to: 'Venezuela', value: 1 },
            { from: 'FinFisher', to: 'Vietnam', value: 1 },
            { from: 'Grayshift', to: 'Belarus', value: 1 },
            { from: 'Grayshift', to: 'United States', value: 1 },
            { from: 'Hacking Team', to: 'Azerbaijan', value: 1 },
            { from: 'Hacking Team', to: 'Brazil', value: 1 },
            { from: 'Hacking Team', to: 'Chile', value: 1 },
            { from: 'Hacking Team', to: 'Colombia', value: 1 },
            { from: 'Hacking Team', to: 'Cyprus', value: 1 },
            { from: 'Hacking Team', to: 'Ecuador', value: 1 },
            { from: 'Hacking Team', to: 'Egypt', value: 1 },
            { from: 'Hacking Team', to: 'Ethiopia', value: 1 },
            { from: 'Hacking Team', to: 'Honduras', value: 1 },
            { from: 'Hacking Team', to: 'Hungary', value: 1 },
            { from: 'Hacking Team', to: 'Italy', value: 1 },
            { from: 'Hacking Team', to: 'Kazakhstan', value: 1 },
            { from: 'Hacking Team', to: 'Lebanon', value: 1 },
            { from: 'Hacking Team', to: 'Malaysia', value: 1 },
            { from: 'Hacking Team', to: 'Mexico', value: 1 },
            { from: 'Hacking Team', to: 'Morocco', value: 1 },
            { from: 'Hacking Team', to: 'Nigeria', value: 1 },
            { from: 'Hacking Team', to: 'Oman', value: 1 },
            { from: 'Hacking Team', to: 'Panama', value: 1 },
            { from: 'Hacking Team', to: 'Poland', value: 1 },
            { from: 'Hacking Team', to: 'Russia', value: 1 },
            { from: 'Hacking Team', to: 'Saudi Arabia', value: 1 },
            { from: 'Hacking Team', to: 'Singapore', value: 1 },
            { from: 'Hacking Team', to: 'Spain', value: 1 },
            { from: 'Hacking Team', to: 'Sudan', value: 1 },
            { from: 'Hacking Team', to: 'Thailand', value: 1 },
            { from: 'Hacking Team', to: 'Turkey', value: 1 },
            { from: 'Hacking Team', to: 'United Arab Emirates', value: 1 },
            { from: 'Hacking Team', to: 'United States', value: 1 },
            { from: 'Hacking Team', to: 'Uzbekistan', value: 1 },
            { from: 'iMyFone', to: 'Myanmar', value: 1 },
            { from: 'Magnet Forensics', to: 'Myanmar', value: 1 },
            { from: 'Magnet Forensics', to: 'United States', value: 1 },
            { from: 'Meiya Pico', to: 'China', value: 1 },
            { from: 'Meiya Pico', to: 'Egypt', value: 1 },
            { from: 'Meiya Pico', to: 'Philippines', value: 1 },
            { from: 'Meiya Pico', to: 'Russia', value: 1 },
            { from: 'Mollitiam', to: 'Colombia', value: 1 },
            { from: 'MSAB', to: 'Hong Kong', value: 1 },
            { from: 'MSAB', to: 'Morocco', value: 1 },
            { from: 'MSAB', to: 'Myanmar', value: 1 },
            { from: 'MSAB', to: 'United States', value: 1 },
            { from: 'NSO Group', to: 'Azerbaijan', value: 1 },
            { from: 'NSO Group', to: 'Bahrain', value: 1 },
            { from: 'NSO Group', to: 'Bangladesh', value: 1 },
            { from: 'NSO Group', to: 'Belgium', value: 1 },
            { from: 'NSO Group', to: 'Djibouti', value: 1 },
            { from: 'NSO Group', to: 'Egypt', value: 1 },
            { from: 'NSO Group', to: 'El Salvador', value: 1 },
            { from: 'NSO Group', to: 'Estonia', value: 1 },
            { from: 'NSO Group', to: 'Germany', value: 1 },
            { from: 'NSO Group', to: 'Ghana', value: 1 },
            { from: 'NSO Group', to: 'Hungary', value: 1 },
            { from: 'NSO Group', to: 'India', value: 1 },
            { from: 'NSO Group', to: 'Indonesia', value: 1 },
            { from: 'NSO Group', to: 'Israel', value: 1 },
            { from: 'NSO Group', to: 'Jordan', value: 1 },
            { from: 'NSO Group', to: 'Kazakhstan', value: 1 },
            { from: 'NSO Group', to: 'Kenya', value: 1 },
            { from: 'NSO Group', to: 'Mexico', value: 1 },
            { from: 'NSO Group', to: 'Morocco', value: 1 },
            { from: 'NSO Group', to: 'Netherlands', value: 1 },
            { from: 'NSO Group', to: 'Panama', value: 1 },
            { from: 'NSO Group', to: 'Poland', value: 1 },
            { from: 'NSO Group', to: 'Rwanda', value: 1 },
            { from: 'NSO Group', to: 'Saudi Arabia', value: 1 },
            { from: 'NSO Group', to: 'Spain', value: 1 },
            { from: 'NSO Group', to: 'Thailand', value: 1 },
            { from: 'NSO Group', to: 'Togo', value: 1 },
            { from: 'NSO Group', to: 'Uganda', value: 1 },
            { from: 'NSO Group', to: 'United Arab Emirates', value: 1 },
            { from: 'NSO Group', to: 'Uzbekistan', value: 1 },
            { from: 'NSO Group', to: 'Zambia', value: 1 },
            { from: 'OpenText', to: 'Myanmar', value: 1 },
            { from: 'Oxygen Software', to: 'Belarus', value: 1 },
            { from: 'Oxygen Software', to: 'Kazakhstan', value: 1 },
            { from: 'Oxygen Software', to: 'Myanmar', value: 1 },
            { from: 'Oxygen Software', to: 'United States', value: 1 },
            { from: 'Oxygen Software', to: 'Uzbekistan', value: 1 },
            { from: 'Paraben', to: 'United States', value: 1 },
            { from: 'Paragon', to: 'United States', value: 1 },
            { from: 'Passware', to: 'Myanmar', value: 1 },
            { from: 'Passware', to: 'United States', value: 1 },
            { from: 'Quadream', to: 'Mexico', value: 1 },
            { from: 'Quadream', to: 'Saudi Arabia', value: 1 },
            { from: 'Quadream', to: 'Singapore', value: 1 },
            { from: 'RCS Labs', to: 'Italy', value: 1 },
            { from: 'RCS Labs', to: 'Kazakhstan', value: 1 },
            { from: 'Resonant', to: 'China', value: 1 },
            { from: 'SalvationData', to: 'Myanmar', value: 1 },
            { from: 'SecurCube', to: 'Myanmar', value: 1 },
            { from: 'Sillicon Forensics', to: 'Myanmar', value: 1 },
            { from: 'Sirchie', to: 'Myanmar', value: 1 },
            { from: 'Susteen', to: 'United States', value: 1 },
            { from: 'SysTools', to: 'Myanmar', value: 1 },
            { from: 'Undisclosed', to: 'Canada', value: 1 },
            { from: 'Zhongke Ronghui Security Technology', to: 'China', value: 1 },
        ]);

        // Make stuff animate on load
        series.appear(1000, 100);
    });
}
</script>

<style lang="scss" scoped>
$gutter: 30px;
$site-width: 1000px;

$black: #000;
$white: #fff;
$teal: #00a79d;
$light-blue: #27aae1;
$red: #ef4136;

$roman-normal-book: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
$roman-italic-book: 'Frutiger Neue LT W06 Book It', Arial, sans-serif;
$roman-normal-bold: 'Frutiger Neue LT W06 Bold', 'Arial Black', Gadget, sans-serif;

//layout basics
.foreground {
    position: relative;
    margin: 0 auto;
    box-sizing: content-box;
    max-width: $site-width;
    padding-left: calc($gutter / 2);
    padding-right: calc($gutter / 2);
}

.section {
    display: block;
    clear: both;
    margin-top: $gutter;
    margin-bottom: $gutter;
}

.gutter-right {
    margin-right: 30px;
}

.gutter-left {
    margin-left: 30px;
}

.zone-title {
    background-color: $black;
    background-image: url(https://ceipimg.s3.amazonaws.com/static/hdr-spyware.jpg);
    background-position: center center;
    background-repeat: no-repeat;
    text-align: center;

    .container-title {
        margin: 20px auto 0 auto;
        padding-top: 180px;
        width: 50%;
    }

    h2 {
        background-color: transparent;
        color: $white;
        display: inline-block;
        margin: $gutter 0;
        padding: calc($gutter / 5) 20px 12px 20px;
        @include type-h2;
    }
}

.interactive {
    background-color: black;
    position: relative;

    #chartdiv {
        width: 100%;
        height: 600px;
        margin: calc(calc($gutter / 2)) 0;
    }

    .legend {
        position: absolute;
        display: block;
        width: 25%;
        max-height: 300px;
        top: 60px;
        right: 35px;

        .legend-value {
            color: $white;
            font-family: $roman-normal-bold;

            .color {
                display: inline-block;
                height: calc(calc($gutter / 2));
                margin-right: calc(calc($gutter / 2));
                width: calc(calc($gutter / 2));
            }

            &.spyware .color {
                background-color: $light-blue;
            }

            &.digital-forensic .color {
                background-color: $red;
            }

            &.country .color {
                background-color: $teal;
            }
        }
    }

    :deep(#info) {
        display: none;

        &.active {
            background-color: #333333;
            border-radius: 0;
            box-shadow: -5px 0px 8px -3px rgba(0, 0, 0, 0.69);
            position: absolute;
            display: block;
            width: 20%;
            max-height: 300px;
            top: 60px;
            left: 35px;
        }

        .description {
            color: $white;
            font-family: $roman-normal-book;
            font-size: 1.2rem;
            overflow-x: clip;
            overflow-y: auto;
            padding: 0 15px 0 15px;
            max-height: 280px;

            .title {
                background-color: #333333;
                color: $white;
                font-family: $roman-normal-bold;
                font-size: 1.3rem;
                margin: -15px 0 0 -15px;
                padding: $gutter 0 0 calc(calc($gutter / 2));
                position: sticky;
                top: 0;
                width: 110%;
                z-index: 6;
            }

            .info-text {
                padding-top: $gutter * 1.5;
                color: $white;
            }

            div {
                margin-bottom: calc(calc($gutter / 2));
            }

            strong {
                color: $white;
                display: inline-block;
                font-family: $roman-normal-bold;
                font-weight: normal;
            }

            em {
                font-family: $roman-italic-book;
            }

            p {
                color: $white;
                padding-left: 15px;

                i {
                    font-family: $roman-italic-book;
                    font-variant: none;
                }
            }
        }

        .close-panel {
            color: $teal;
            cursor: pointer;
            font-family: $roman-normal-bold;
            font-size: 2rem;
            font-weight: normal;
            top: -15px;
            left: -9px;
            position: absolute;
            text-transform: uppercase;
            z-index: 7;

            &:after {
                border: none;
            }

            .icon-cancel-circle {
                // padding-left: 1em;
            }
        }
    }
}

p {
    margin-bottom: 10px;
}

h4 {
    margin-bottom: $gutter;
}

.center {
    text-align: center;
}

.button-big {
    display: inline-block;
    margin: 0 10px 15px 0;
    text-transform: uppercase;
    font-size: 120%;
    box-shadow: 0 10px 8px -8px rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    line-height: 1;
    padding: 0.9rem 1.5rem 1rem;
    background: #062544;
    color: white;
    -webkit-transition: background-color 0.2s cubic-bezier(0.46, 0.6, 0.5, 0.36);
    -o-transition: background-color 0.2s cubic-bezier(0.46, 0.6, 0.5, 0.36);
    transition: background-color 0.2s cubic-bezier(0.46, 0.6, 0.5, 0.36);
    @include type-body;
}

@include media-query(phone) {
    .zone-title h2 {
        padding: 0;
    }
}
</style>
